import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useTheme,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  KeyboardArrowDown,
  Refresh,
} from '@mui/icons-material';
import BookMyOpAPI from '../../services/BookMyOpapi';
import DoctorCard from '../../components/DoctorCard';

const SenioritySort = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleDoctors, setVisibleDoctors] = useState(4); // Show 4 initially
  const [showingMore, setShowingMore] = useState(false);
  
  // Ref to prevent duplicate calls in React Strict Mode
  const hasFetched = useRef(false);

  // Fetch senior doctors data from API
  const fetchDoctors = useCallback(async () => {
    // Prevent duplicate calls in React Strict Mode
    if (hasFetched.current) {
      return;
    }
    
    try {
      setLoading(true);
      hasFetched.current = true;
      
      const response = await BookMyOpAPI.getSeniorDoctors({ 
        page: 1,
        pageSize: 20
      });
      
      // Handle nested response structure: response.results.data.doctors
      if (response && response.results && response.results.data && response.results.data.doctors) {
        // Get first 12 doctors for the grid
        const doctorData = response.results.data.doctors.slice(0, 12);
        setDoctors(doctorData);
        setError(null);
      } else if (response && response.data && response.data.doctors) {
        // Handle alternative response format
        const doctorData = response.data.doctors.slice(0, 12);
        setDoctors(doctorData);
        setError(null);
      } else if (response && response.results && Array.isArray(response.results)) {
        // Handle direct array in results
        const doctorData = response.results.slice(0, 12);
        setDoctors(doctorData);
        setError(null);
      } else {
        setError('No senior doctors found');
        setDoctors([]);
      }
    } catch (err) {
      // Check if error is due to missing location
      const errorMessage = err.message?.includes('location') || err.message?.includes('Please set your location')
        ? 'Please set your location first to view senior doctors'
        : 'Failed to fetch senior doctors. Please try again later.';
      setError(errorMessage);
      console.error('Error fetching senior doctors:', err);
      hasFetched.current = false; // Reset on error to allow retry
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load - with protection against React Strict Mode
  useEffect(() => {
    if (doctors.length === 0) {
      fetchDoctors();
    }
  }, []); // Empty dependency array - only run once on mount

  // Listen for location updates
  useEffect(() => {
    const handleLocationUpdate = () => {
      console.log('Location updated, refreshing senior doctors...');
      hasFetched.current = false; // Allow re-fetch
      setDoctors([]); // Clear current data
      fetchDoctors();
    };

    window.addEventListener('locationUpdated', handleLocationUpdate);
    return () => window.removeEventListener('locationUpdated', handleLocationUpdate);
  }, [fetchDoctors]);

  const handleBookAppointment = (doctorId) => {
    navigate(`/doctors/${doctorId}`);
  };

  const handleShowMore = () => {
    setShowingMore(true);
    setTimeout(() => {
      setVisibleDoctors(prev => Math.min(prev + 4, doctors.length));
      setShowingMore(false);
    }, 300);
  };

  const handleRefresh = () => {
    setVisibleDoctors(4);
  };

  if (loading) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: theme.palette.background.default }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={50} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: theme.palette.background.default }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (doctors.length === 0) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: theme.palette.background.default }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No senior doctors available at the moment.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 2, md: 3 } }}>
      <Container maxWidth="xxl">
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: { xs: 2, md: 3 },  }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: theme.palette.text.primary,
              textTransform:'uppercase',
              textAlign:'center',
            }}
          >
            Experienced Doctors
          </Typography>
          
          {visibleDoctors > 4 && (
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>

        {/* Responsive Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: 2, md: 2.5 },
            px: { xs: 1, md: 2 },
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {doctors.slice(0, visibleDoctors).map((doctor, index) => (
            <DoctorCard 
              key={doctor.id} 
              doctor={doctor} 
              index={index} 
              onClick={handleBookAppointment}
            />
          ))}
        </Box>

        {/* Show More / View All Actions */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          {visibleDoctors < doctors.length ? (
            <Button
              variant="contained"
              onClick={handleShowMore}
              disabled={showingMore}
              startIcon={showingMore ? <CircularProgress size={16} /> : <KeyboardArrowDown />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                mr: 2,
                background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                '&:hover': {
                  background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                },
              }}
            >
              {showingMore ? 'Loading...' : `Show More (${doctors.length - visibleDoctors} left)`}
            </Button>
          ) : null}
          
          <Button
            variant="outlined"
            onClick={() => navigate('/find-doctors')}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            View All Doctors
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SenioritySort;

